import { X } from 'lucide-react';
import { Badge } from './ui/badge';
import {
  ACTIVITY_STATUS_OPTIONS,
  PLAY_TYPE_OPTIONS,
  PREP_NEEDED_OPTIONS,
  DURATION_OPTIONS,
  AGE_GROUP_OPTIONS,
  type ActivityFilters,
  type Tag,
  type Transition,
  type ActivityCategory,
} from '../types/activity';

interface ActiveFiltersProps {
  filters: ActivityFilters;
  onClearFilter: (key: keyof ActivityFilters) => void;
  onClearAll: () => void;
  hasActiveFilters: boolean;
  availableTags: Tag[];
  onRemoveTag: (tagId: number) => void;
  availableTransitions: Transition[];
  onRemoveTransition: (transitionId: number) => void;
  onRemoveAgeGroup: (key: string) => void;
  availableCategories: ActivityCategory[];
  onRemoveCategory: (id: number) => void;
  jobContext?: { id: string; createdAt: string } | null;
}

function formatJobDate(dateStr: string): string {
  const d = new Date(dateStr);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ActiveFilters({
  filters,
  onClearFilter,
  onClearAll,
  hasActiveFilters,
  availableTags,
  onRemoveTag,
  availableTransitions,
  onRemoveTransition,
  onRemoveAgeGroup,
  availableCategories,
  onRemoveCategory,
  jobContext,
}: ActiveFiltersProps) {
  if (!hasActiveFilters) {
    return null;
  }

  const getFilterLabel = (key: keyof ActivityFilters, value: string | number): string => {
    switch (key) {
      case 'status':
        return ACTIVITY_STATUS_OPTIONS.find((o) => o.value === value)?.label || String(value);
      case 'playType':
        return PLAY_TYPE_OPTIONS.find((o) => o.value === value)?.label || String(value);
      case 'prepNeeded':
        return PREP_NEEDED_OPTIONS.find((o) => o.value === value)?.label || String(value);
      case 'duration':
        return DURATION_OPTIONS.find((o) => o.value === value)?.label || `${value} min`;
      case 'search':
        return `Search: "${value}"`;
      default:
        return String(value);
    }
  };

  const getTagLabel = (tagId: number) => {
    return availableTags.find((t) => parseInt(t.id) === tagId)?.label || `Tag ${tagId}`;
  };

  const getTransitionLabel = (transitionId: number) => {
    return availableTransitions.find((t) => parseInt(t.id) === transitionId)?.label || `Period ${transitionId}`;
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-body-sm text-admin-slate-500">Active filters:</span>

      {filters.search && (
        <FilterBadge
          label={getFilterLabel('search', filters.search)}
          onRemove={() => onClearFilter('search')}
        />
      )}

      {filters.status && (
        <FilterBadge
          label={getFilterLabel('status', filters.status)}
          onRemove={() => onClearFilter('status')}
        />
      )}

      {filters.playType && (
        <FilterBadge
          label={getFilterLabel('playType', filters.playType)}
          onRemove={() => onClearFilter('playType')}
        />
      )}

      {filters.prepNeeded && (
        <FilterBadge
          label={getFilterLabel('prepNeeded', filters.prepNeeded)}
          onRemove={() => onClearFilter('prepNeeded')}
        />
      )}

      {filters.duration !== '' && (
        <FilterBadge
          label={getFilterLabel('duration', filters.duration)}
          onRemove={() => onClearFilter('duration')}
        />
      )}

      {filters.categoryIds.map((catId) => (
        <FilterBadge
          key={`category-${catId}`}
          label={`Category: ${availableCategories.find((c) => parseInt(c.id) === catId)?.name ?? catId}`}
          onRemove={() => onRemoveCategory(catId)}
        />
      ))}

      {filters.tagIds.map((tagId) => (
        <FilterBadge
          key={tagId}
          label={getTagLabel(tagId)}
          onRemove={() => onRemoveTag(tagId)}
          variant="tag"
        />
      ))}

      {filters.transitionIds.map((transitionId) => (
        <FilterBadge
          key={`transition-${transitionId}`}
          label={getTransitionLabel(transitionId)}
          onRemove={() => onRemoveTransition(transitionId)}
          variant="transition"
        />
      ))}

      {filters.ageGroupKeys.map((key) => {
        const option = AGE_GROUP_OPTIONS.find((o) => o.key === key);
        return (
          <FilterBadge
            key={`age-${key}`}
            label={`Age: ${option?.label ?? key} yrs`}
            onRemove={() => onRemoveAgeGroup(key)}
            variant="tag"
          />
        );
      })}

      {/* AI Generated chip */}
      {filters.isAiGenerated && (
        <FilterBadge
          label="AI Generated"
          onRemove={() => onClearFilter('isAiGenerated')}
        />
      )}

      {/* Generation job context chip */}
      {filters.generationJobId && (
        <FilterBadge
          label={
            jobContext
              ? `Generated by job: ${formatJobDate(jobContext.createdAt)} · ${filters.generationJobId.slice(0, 4)}`
              : `Generated by job: ${filters.generationJobId.slice(0, 4)}`
          }
          onRemove={() => onClearFilter('generationJobId')}
        />
      )}

      <button
        onClick={onClearAll}
        className="text-body-sm text-admin-accent-blue hover:text-admin-accent-blue/80 transition-colors"
      >
        Clear all
      </button>
    </div>
  );
}

interface FilterBadgeProps {
  label: string;
  onRemove: () => void;
  variant?: 'default' | 'tag' | 'transition';
}

function FilterBadge({ label, onRemove, variant = 'default' }: FilterBadgeProps) {
  const variantStyles =
    variant === 'tag'
      ? 'bg-admin-accent-purple/10 text-admin-accent-purple border-admin-accent-purple/20'
      : variant === 'transition'
      ? 'bg-amber-500/10 text-amber-500 border-amber-500/20'
      : 'bg-admin-accent-blue/10 text-admin-accent-blue border-admin-accent-blue/20';

  return (
    <Badge
      variant="secondary"
      className={`${variantStyles} border pl-3 pr-1 py-1 gap-1`}
    >
      <span>{label}</span>
      <button
        onClick={onRemove}
        className="ml-1 hover:bg-white/10 rounded-full p-0.5 transition-colors"
        aria-label={`Remove ${label} filter`}
      >
        <X className="w-3 h-3" />
      </button>
    </Badge>
  );
}
